import type {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

import {TriggerType} from './RoutineTypes';
import {getRandomWeeklyScheduleDefaults} from './RoutineUtils';
import {FIELD_CHANGED, OBJECT_CREATED} from './triggerConstants';
import {getDefaultTimeTriggerConfig, RoutineFormState} from './useRoutineForm';

export interface RoutineExample {
  id: string;
  label: string;
  icon: BenchlingIconName;
  formState: RoutineFormState;
}

// Hardcoded, tenant-agnostic examples that prefill the create panel. Trigger configs mirror the
// backend EVENT_TRIGGER_REGISTRY (object_type/field/to_values). No schema_id is set because it is
// tenant-specific; examples prefill to the "all objects of this type" scope and the user narrows it.
export const ROUTINE_EXAMPLES: Array<RoutineExample> = [
  {
    id: 'weekly-project-summary',
    label: 'Weekly project summary',
    icon: 'calendar-days',
    formState: {
      name: 'Weekly project summary',
      description: '',
      prompt:
        'Summarize the state of experiments in the project over the past week, highlighting completed work, in-progress experiments, and anything that appears blocked.',
      fileBlocks: [],
      triggerType: TriggerType.TIME_BASED,
      triggerConfig: {
        ...getDefaultTimeTriggerConfig(),
        freq_type: 'weekly',
        recurrence_interval: 1,
        // Randomize the weekday and hour so users starting from this example don't all schedule
        // at the same default time (previously Monday 8am), spreading out scheduled load.
        ...getRandomWeeklyScheduleDefaults(),
      },
    },
  },
  {
    id: 'flag-duplicate-studies',
    label: 'Flag duplicate studies at execution',
    icon: 'copy',
    formState: {
      name: 'Flag duplicate studies at execution',
      description: '',
      prompt:
        'A study was just moved into execution. Before work begins, check whether a similar or equivalent study has already been run. Compare its objective, design, and key parameters against existing studies of the same schema, and for each close match describe how similar it is and why. If you find likely duplicates, recommend whether this study should proceed, be modified, or reference the prior work. If nothing similar turns up, say so.',
      fileBlocks: [],
      triggerType: TriggerType.EVENT_BASED,
      triggerConfig: {
        object_type: 'Study',
        trigger_kind: FIELD_CHANGED,
        field: 'phase',
        to_values: ['EXECUTION'],
      },
    },
  },
  {
    id: 'flag-duplicate-sequences',
    label: 'Flag duplicate sequences on creation',
    icon: 'dna-magnifying-glass',
    formState: {
      name: 'Flag duplicate sequences on creation',
      description: '',
      prompt:
        'A new DNA sequence has been created. Search existing sequences I have access to and identify (a) exact duplicates, including reverse-complement matches, and (b) highly similar near-matches or variants. For each match, describe how similar it is, what it is (name, schema, folder/project), and why it looks like a match. If you find exact duplicates or close variants, recommend whether to reuse the existing sequence, register this one as a distinct variant, or check with the owner before proceeding. If nothing similar turns up, say so.',
      fileBlocks: [],
      triggerType: TriggerType.EVENT_BASED,
      triggerConfig: {object_type: 'DnaSequence', trigger_kind: OBJECT_CREATED},
    },
  },
];
